import React, { useCallback, useMemo, useState } from "react";
import { useAuth } from "@/legacy/redux";
import { useIsSuperAdmin } from "../../auth/permissions";
import { config } from "../../config";
import InternalDebuggerNodeGraph, { NodeGraphSolveContext } from "../../components/internal-debugger/InternalDebuggerNodeGraph";
import InternalDebuggerJsonInput from "../../components/internal-debugger/InternalDebuggerJsonInput";
import InternalDebuggerRelationshipGraph from "../../components/internal-debugger/InternalDebuggerRelationshipGraph";
import InternalDebuggerGraphExtractor from "../../components/internal-debugger/InternalDebuggerGraphExtractor";
import InternalDebuggerNodeInfoPanel, { SelectedNodeContext } from "../../components/internal-debugger/InternalDebuggerNodeInfoPanel";
import "./InternalDebuggerPage.scss";
import { Release } from "@packages/commons";
import { getAccessToken } from "@/features/auth/storage";
import { handleTenancyHttpResponse, requireActiveTenancy } from "@/features/tenancy";

// Types from the original debugger
export interface NodeValue {
  type: string;
  elements: NodeValue[];
  value: any;
  attributeId: string;
}

export interface GraphNode {
  v: string;
  value: {
    conditions: NodeValue[];
    value: NodeValue | null;
    input: any;
    derived: any;
    error: any;
    hidden?: boolean;
  };
}


export interface PostSolveResponse {
  goal?: string;
  resolved: boolean;
  processingTime: number;
  graph?: { nodes: GraphNode[] };
}

// Helper functions from the original debugger
export const isOk = <T, E>(result: any): result is { Ok: T } => {
  return (result as any)?.Ok !== undefined;
};

export const idSort = (a: GraphNode, b: GraphNode) => {
  const aParts = a.v.split("/");
  const bParts = b.v.split("/");

  let i = -1;
  while (true) {
    i++;
    if (i >= aParts.length || i >= bParts.length) {
      return aParts.length > bParts.length ? 1 : -1;
    }
    const aPart = parseInt(aParts[i]) || aParts[i];
    const bPart = parseInt(bParts[i]) || bParts[i];

    if (aPart === bPart) {
      continue;
    } else {
      return aPart > bPart ? 1 : -1;
    }
  }
};

// Rules Engine interface
interface RulesEngine {
  solve(options: any, releaseHash: string, externalData: any, state: any): Promise<any>;
}

// Load rules engine like the interview SDK
const loadRulesEngine = async (): Promise<RulesEngine> => {
  const accessToken = getAccessToken();

  const headers: Record<string, string> = {
    "X-TENANCY": requireActiveTenancy(),
  };

  // Add auth token if available
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${config.BACKEND_URL}/decisionapi/rules-engine-script?checksum=debugger`, {
    headers,
  });

  if (!response.ok) {
    await handleTenancyHttpResponse(response);
    throw new Error(`Failed to load rules engine: ${response.status}`);
  }

  const script = await response.text();
  // biome-ignore lint: https://esbuild.github.io/content-types/#direct-eval
  return (0, eval)(script);
};

const InternalDebuggerPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = useIsSuperAdmin();

  // Check if user has access
  if (!isSuperAdmin) {
    return (
      <div style={{ padding: "24px" }}>
        <h1 style={{ color: "red" }}>Access Denied</h1>
        <p>You need super admin privileges to access the debugger.</p>
      </div>
    );
  }

  const [release, setRelease] = useState<Release>();
  const [input, setInput] = useState<any>();
  const [selectedNode, setSelectedNode] = useState<string | undefined>();
  const [solveResult, setSolveResult] = useState<PostSolveResponse | null>(null);
  const [isSolving, setIsSolving] = useState(false);
  const [rulesEngine, setRulesEngine] = useState<RulesEngine | null>(null);
  const [wasmLoaded, setWasmLoaded] = useState(false);

  // Load rules engine on mount
  React.useEffect(() => {
    const loadEngine = async () => {
      try {
        const engine = await loadRulesEngine();
        setRulesEngine(engine);
        setWasmLoaded(true);
      } catch (error) {
        console.error("Failed to load rules engine:", error);
      }
    };
    loadEngine();
  }, []);


  const handleSolveWasm = useCallback(async () => {
    if (!release || !input || !rulesEngine) return;

    setIsSolving(true);
    const startTime = Date.now();

    try {
      // Use the rules engine to solve like the original debugger
      const result = await rulesEngine.solve(
        { input, response_elements: [{type: "graph", debug: true}], debug: true },
        new Date().toString(),
        {
          getRelease: () => release,
          getDataTableValue: () => {},
          getSpreadsheetCell: () => {},
        },
        null
      );

      const processingTime = Date.now() - startTime;
      console.log(`Executed WASM solve in ${processingTime}ms`);

      setSolveResult({
        goal: result.goal,
        resolved: true,
        processingTime,
        graph: result.graph,
      });
    } catch (error) {
      console.error("WASM Solve error:", error);
      setSolveResult({
        goal: undefined,
        resolved: false,
        processingTime: Date.now() - startTime,
        graph: undefined
      });
    } finally {
      setIsSolving(false);
    }
  }, [release, input, rulesEngine]);

  const selectedNodeContext = useMemo(() => ({
    selected: selectedNode,
    setSelected: setSelectedNode,
  }), [selectedNode, setSelectedNode]);

  const resolveTime = solveResult ? solveResult.processingTime : 0;

  return (
    <SelectedNodeContext.Provider value={selectedNodeContext}>
      <NodeGraphSolveContext.Provider value={solveResult || undefined}>
        <div className="InternalDebugger">
          <div className="InternalDebugger__container">
            {/* Left Panel - Controls */}
            <div style={{ minWidth: "400px", flex: "auto", overflow: "auto", border: "1px solid #ccc", padding: "16px" }}>
              <h3>Configuration</h3>

              <InternalDebuggerJsonInput
                onValidateValue={(graph) => graph.rule_graph}
                onValidValueChange={setRelease}
                label="Graph"
                id="graph"
              />

              <hr style={{ margin: "16px 0" }} />

              <InternalDebuggerJsonInput
                onValidValueChange={setInput}
                label="Input"
                id="input"
              />

              <hr style={{ margin: "16px 0" }} />


              <InternalDebuggerGraphExtractor />

              <hr style={{ margin: "16px 0" }} />

              {/* Solve Controls */}
              <div style={{ marginTop: "16px" }}>
                <h3>Execute</h3>

                {solveResult && (
                  <div style={{ marginBottom: "16px", padding: "8px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                    <div>
                      {solveResult.resolved ? "✅" : "⚠️"} {resolveTime.toFixed(2)}ms
                    </div>
                  </div>
                )}

                <button
                  onClick={handleSolveWasm}
                  disabled={!release || !input || !wasmLoaded || isSolving}
                >
                  {isSolving ? "Solving..." : wasmLoaded ? "Solve (WASM)" : "Loading WASM..."}
                </button>
              </div>
            </div>

              <InternalDebuggerNodeGraph
                release={release}
                solveResult={solveResult}
                selectedNodeContext={selectedNodeContext}
              />

              <InternalDebuggerNodeInfoPanel graph={release} />
          </div>
        </div>
      </NodeGraphSolveContext.Provider>
    </SelectedNodeContext.Provider>
  );
};

export default InternalDebuggerPage;
